// src/services/backgroundRefresh.js
//
// Tabs refresh themselves after a cache-version bump, when nobody is using them.
// Erik, 2026-09-05: "do what the banner is saying a user should do, but automatically in the
// background by refreshing everything when it is not in view." The policy (what to do) is pure and
// lives in src/utils/cacheRefreshPolicy.js; this file is the browser wiring (when to look, how to
// act). Spec + acceptance: docs/bugs/firestore_cache_recovery_banner_loop_BUG.md.
//
// Signals this tab watches
//   - globalSettings/appCacheVersion (live): a bump makes this tab "stale" and, if the tab is the
//     first to see it, writes the shared pending-reset marker and acknowledges the version.
//   - localStorage `storage` events: another tab acknowledged a version or finished the reset.
//   - visibility, input activity, a route change starting, and a 15 s tick.
//   - the Firestore database itself: a passive connection hears a peer's delete request
//     (versionchange) so this tab can park before the SDK terminates under it.
//
// Actions (cacheRefreshPolicy.decideRefreshAction)
//   reload / navigate  new bundle, same mode
//   park               reload into memory-only Firestore so this tab stops holding the database
//   reset              the C-09 single-deletion path (clearFirestoreCache coordinator), then reload
//   restore            a parked tab returns to persistent cache once the reset is done
//
// Nothing here ever runs while a blocker says "not now" (a live call, a walkie call, a record under
// edit, an upload). Blockers register themselves with registerRefreshBlocker().

import { doc, waitForPendingWrites } from 'firebase/firestore'
import { onSnapshot, releaseFirestore } from '@/services/sessionListeners'
import { db, firebaseApp, useMemoryFirestoreCache } from '@/services/firebaseconfig'
import { getCacheVersion, setCacheVersion } from '@/utils/cacheVersionCheck'
import {
  activateFirestoreMemoryFallback,
  clearFirestoreCacheResetMarker,
  clearFirestoreMemoryFallback,
  countLiveFirestoreClients,
  getFirestoreMemoryFallback,
  inspectFirestorePersistenceState,
  isFirestoreCacheResetInProgress,
  isFirestoreCacheResetMarkerStale,
  watchFirestoreDatabaseDeletion,
  withCacheBootstrapTimeout,
} from '@/utils/clearFirestoreCache'
import {
  CACHE_RESET_PENDING_KEY,
  PARKED_REASON_CODE,
  REFRESH_TICK_MS,
  decideRefreshAction,
  idleThresholdFor,
  isFailureFallback,
  isQuiet,
  jitterMs,
  readPendingReset,
  writePendingReset,
} from '@/utils/cacheRefreshPolicy'
import { useFrontendFlagsStore } from '@/stores/frontendFlagsStore'
import {
  decideChunkRecovery,
  readChunkReloadAttempt,
  writeChunkReloadAttempt,
} from '@/utils/chunkLoadRecovery'
import { useToast } from '@/composables/useToast'

const LOCAL_VERSION_KEY = 'gse_cache_version'
const PARK_DRAIN_TIMEOUT_MS = 6000
// A rebuild is drain (≤ 6 s) + terminate + delete. Past this the delete is pending on a connection
// we cannot see (a frozen peer): park, leave the reset marker standing so new tabs boot
// memory-only and never queue behind that request (C-09).
const RESET_DEADLINE_MS = 20000
const ACTIVITY_EVENTS = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart', 'scroll']

// ── blockers ────────────────────────────────────────────────────────────────

const blockers = new Map()

/**
 * Register a reason this tab must not be refreshed right now. `isActive` is polled; a throwing
 * blocker counts as active (fail closed). Returns the unregister function.
 */
export function registerRefreshBlocker(name, isActive) {
  if (!name || typeof isActive !== 'function') return () => {}
  blockers.set(name, isActive)
  return () => {
    if (blockers.get(name) === isActive) blockers.delete(name)
  }
}

export function activeRefreshBlockers() {
  const active = []
  for (const [name, isActive] of blockers) {
    try {
      if (isActive()) active.push(name)
    } catch {
      active.push(name)
    }
  }
  return active
}

// ── state ───────────────────────────────────────────────────────────────────

const state = {
  started: false,
  coordinator: null,
  // The version this tab's bundle and in-memory data were built for. Anything newer makes it stale.
  bootedVersion: 0,
  knownVersion: 0,
  lastActivityAt: Date.now(),
  inFlight: false,
  scheduled: null,
  tickTimer: null,
  unsubscribeVersion: null,
  watcher: null,
}

function log(message, extra) {
  if (extra !== undefined) console.log(`[backgroundRefresh] ${message}`, extra)
  else console.log(`[backgroundRefresh] ${message}`)
}

function projectId() {
  return firebaseApp?.options?.projectId || 'gse-main-site'
}

function currentMode() {
  return useMemoryFirestoreCache ? 'memory' : 'persistent'
}

function noteActivity() {
  state.lastActivityAt = Date.now()
}

function learnVersion(version, { forceReload = false, reason = '' } = {}) {
  const v = Number(version)
  if (!Number.isSafeInteger(v) || v <= state.knownVersion) return false
  state.knownVersion = v
  if (v > getCacheVersion()) {
    // First tab to see the bump: record that the shared cache needs rebuilding, THEN acknowledge
    // the version (a peer that reads local == v may rely on the marker being there).
    writePendingReset(undefined, { version: v, forceReload, reason })
    setCacheVersion(v)
    log(`cache version ${v} seen; pending reset recorded`, { forceReload, reason })
  }
  return true
}

// ── the passive connection: hear a peer's delete, get out of the way ────────

function parkAndReload(reasonCode, message) {
  activateFirestoreMemoryFallback(reasonCode, message)
  window.location.reload()
}

function attachDeletionWatcher() {
  if (state.watcher || currentMode() !== 'persistent') return
  state.watcher = watchFirestoreDatabaseDeletion({
    projectId: projectId(),
    onDeleteRequested: () => {
      state.watcher = null
      const blockedBy = activeRefreshBlockers()
      if (blockedBy.length > 0) {
        // The connection is already released so the peer's delete can finish, but a reload would
        // drop a live call or an edit. The SDK terminates this tab's Firestore on the same event;
        // the page keeps what it has until the user moves on.
        console.warn(
          '[backgroundRefresh] a peer is rebuilding the shared cache while this tab is busy; not reloading',
          blockedBy
        )
        return
      }
      log('a peer is rebuilding the shared cache; parking')
      parkAndReload(
        PARKED_REASON_CODE,
        'Parked: another tab is rebuilding the shared offline cache.'
      )
    },
  })
}

function detachDeletionWatcher() {
  if (!state.watcher) return
  state.watcher.close()
  state.watcher = null
}

// ── evaluation ──────────────────────────────────────────────────────────────

async function evaluate({ navigating = false } = {}) {
  let enabled = true
  try {
    enabled = useFrontendFlagsStore().isFlagOn('backgroundRefresh_enabled', true)
  } catch {
    enabled = true
  }
  const pending = readPendingReset()
  const fallback = getFirestoreMemoryFallback()
  const mode = currentMode()
  // A memory-mode tab returns to persistence on its own unless it got there through a failure.
  const parked = mode === 'memory' && !isFailureFallback(fallback)

  const clients = await countLiveFirestoreClients({ projectId: projectId() })
  const storage = inspectFirestorePersistenceState()
  const clientsKnown = clients.status === 'ok' || clients.status === 'no-database'
  const liveClients = clientsKnown ? clients.live.length : Number.POSITIVE_INFINITY
  const pendingMutations =
    storage.status === 'ok' ? storage.mutationKeys.length : Number.POSITIVE_INFINITY
  const deletePending = clients.status === 'blocked'

  const blockedBy = activeRefreshBlockers()
  const idleMs = Date.now() - state.lastActivityAt
  const quiet = isQuiet({
    visibilityState: typeof document !== 'undefined' ? document.visibilityState : 'visible',
    idleMs,
    idleThresholdMs: idleThresholdFor(pending),
  })
  const stale = state.knownVersion > state.bootedVersion
  const decision = decideRefreshAction({
    enabled,
    blocked: blockedBy.length > 0,
    quiet,
    navigating,
    stale,
    pending,
    mode,
    parked,
    liveClients,
    pendingMutations,
  })
  return {
    ...decision,
    pending,
    mode,
    blockedBy,
    stale,
    liveClients,
    deletePending,
    clientStatus: clients.status,
  }
}

// ── actions ─────────────────────────────────────────────────────────────────

function go(target) {
  if (target) window.location.assign(target)
  else window.location.reload()
}

async function drainQuietly() {
  try {
    await withCacheBootstrapTimeout(waitForPendingWrites(db), PARK_DRAIN_TIMEOUT_MS)
  } catch {
    // Pending writes stay queued in IndexedDB and their mutation markers keep the delete blocked.
  }
}

// The version listener errors with "Firestore shutting down" once terminate() runs; it has done
// its job by then (this tab is about to reload), so detach it first and keep the console clean.
function stopVersionListener() {
  try {
    state.unsubscribeVersion?.()
  } catch {
    // already gone
  }
  state.unsubscribeVersion = null
}

// A reload cannot reliably remove this tab's clientMetadata record (IndexedDB during unload), so
// a parked tab's previous incarnation would look alive to its peers for up to the 3-minute
// threshold. Terminating first runs the SDK's shutdown in a normal context and deletes the
// record now (measured 2026-09-06: two parked tabs waited 141 s on their own old records).
// releaseFirestore closes every tracked listener first: terminating under ~40 open listeners made
// each report "Firestore shutting down" (docs/bugs/cache_park_listener_errors_BUG.md).
async function terminateQuietly() {
  try {
    await withCacheBootstrapTimeout(releaseFirestore(db), PARK_DRAIN_TIMEOUT_MS)
  } catch {
    // The reload that follows closes the connection either way.
  }
}

async function perform(decision, { target = null } = {}) {
  const { action, pending, mode } = decision
  switch (action) {
    case 'reload':
    case 'navigate':
      log(`${action}: ${decision.reason}`)
      go(target)
      return true
    case 'park':
      log('park: releasing the shared cache for the rebuild', { liveClients: decision.liveClients })
      detachDeletionWatcher()
      stopVersionListener()
      await drainQuietly()
      await terminateQuietly()
      parkAndReload(
        PARKED_REASON_CODE,
        'Parked: this tab released the shared offline cache so it can be rebuilt. It returns to the persistent cache on its own.'
      )
      return true
    case 'restore':
      if (decision.deletePending) return false // a delete is pending on the database; opening now would queue behind it
      if (isFirestoreCacheResetInProgress()) {
        // A reset is running elsewhere: wait for the next tick. Unless the marker is a leftover
        // (older than a real reset lasts) AND the database opened without blocking just now.
        if (!isFirestoreCacheResetMarkerStale()) return false
        log('restore: clearing a stale reset marker')
        clearFirestoreCacheResetMarker()
      }
      log('restore: returning to the persistent cache')
      clearFirestoreMemoryFallback()
      go(target)
      return true
    case 'reset': {
      if (!state.coordinator || !pending) return false
      log(`reset: ${decision.reason}`, { version: pending.version, mode })
      detachDeletionWatcher() // our own passive connection must not block our own delete
      if (mode === 'persistent') stopVersionListener() // the coordinator terminates this client
      let result
      try {
        result = await withCacheBootstrapTimeout(
          state.coordinator.reset({
            remoteVersion: pending.version,
            ownsPersistentClient: mode === 'persistent',
          }),
          RESET_DEADLINE_MS
        )
      } catch (error) {
        state.coordinator.abort()
        log('reset did not finish in time; parking', error?.message || error)
        parkAndReload(PARKED_REASON_CODE, 'Parked: the cache rebuild did not finish in time.')
        return true
      }
      if (result.status === 'cleared' || result.status === 'already-current') {
        if (mode === 'memory') {
          if (isFirestoreCacheResetInProgress()) clearFirestoreCacheResetMarker()
          clearFirestoreMemoryFallback()
        }
        log(`reset ${result.status}; reloading`)
        go(target)
        return true
      }
      if (result.terminated) {
        // Firestore is gone in this tab and the delete did not happen: park so the next boot
        // can try again from a clean instance.
        log('reset stopped after terminate; parking', result.status)
        parkAndReload(
          PARKED_REASON_CODE,
          'Parked after an interrupted cache rebuild; this tab returns to the persistent cache on its own.'
        )
        return true
      }
      log(`reset not run: ${result.status}`, result.stage || '')
      attachDeletionWatcher()
      return false
    }
    default:
      return false
  }
}

function scheduleQuietAction() {
  if (state.scheduled || state.inFlight) return
  const delay = jitterMs()
  state.scheduled = setTimeout(async () => {
    state.scheduled = null
    if (state.inFlight) return
    state.inFlight = true
    try {
      // Conditions may have changed during the jitter (the user came back, a call started).
      const decision = await evaluate()
      if (decision.action !== 'none') await perform(decision)
    } catch (error) {
      console.warn('[backgroundRefresh] action failed', error)
    } finally {
      state.inFlight = false
    }
  }, delay)
}

let ticking = false
async function tick() {
  if (!state.started || state.inFlight || state.scheduled || ticking) return
  ticking = true
  try {
    const decision = await evaluate()
    if (decision.action !== 'none') scheduleQuietAction()
  } catch (error) {
    console.warn('[backgroundRefresh] tick failed', error)
  } finally {
    ticking = false
  }
}

/**
 * Return true only when refresh takes over navigation. The cached verdict is a
 * hint: a call or edit can start before the next tick. Await a fresh decision
 * so blockers and failures fall through to the normal router guards.
 */
export async function maybeRefreshOnNavigation(to, from) {
  if (!state.started || state.inFlight) return false
  if (!from || !from.name) return false // the boot navigation
  if (!to || to.name === 'SignedOut' || to.meta?.public) return false
  if (!state.lastVerdict || state.lastVerdict.action === 'none') return false
  if (activeRefreshBlockers().length) return false
  const target = to.fullPath || null
  state.inFlight = true
  try {
    const decision = await evaluate({ navigating: true })
    if (decision.action === 'none' || activeRefreshBlockers().length) return false
    return await perform(decision, { target })
  } catch (error) {
    console.warn('[backgroundRefresh] navigation refresh failed', error)
    return false
  } finally {
    state.inFlight = false
  }
}

/**
 * A navigation failed because this tab's build is gone from hosting (a deploy since the tab loaded;
 * docs/bugs/stale_tab_missing_chunk_BUG.md). Load the new build at the page asked for, once, under
 * the same blockers as every other refresh here. The route's leave guards have already run, so the
 * page being left has agreed to go. Returns true when the browser takes over.
 */
export function recoverFromMissingChunk(to, error) {
  const target = to?.fullPath || window.location.pathname + window.location.search + window.location.hash
  const storage = browserSessionStorage()
  const decision = decideChunkRecovery({
    target,
    blockedBy: activeRefreshBlockers(),
    lastAttempt: readChunkReloadAttempt(storage),
    canRecord: Boolean(storage),
    online: navigator.onLine !== false,
  })
  if (decision.action === 'reload' && writeChunkReloadAttempt(storage, { target, at: Date.now() })) {
    console.warn(`[backgroundRefresh] ${target} is not in this tab's build (a deploy since it loaded); loading the new build`)
    go(target)
    return true
  }
  console.warn(`[backgroundRefresh] ${target} could not load from this tab's build`, decision, error)
  // Stays up until dismissed: the user has to act, and the page behind it may be the one that failed.
  useToast().warning(CHUNK_RECOVERY_MESSAGES[decision.action] || CHUNK_RECOVERY_MESSAGES['gave-up'], {
    timeout: false,
  })
  return false
}

const CHUNK_RECOVERY_MESSAGES = {
  blocked:
    'A newer version of the CRM is out and this page needs it. Refresh the page when you finish what you are doing.',
  offline: 'This page could not load. Check your connection, then try again.',
  'gave-up': 'This page could not load. Refresh the page to try again.',
}

function browserSessionStorage() {
  try {
    return window.sessionStorage || null
  } catch {
    return null
  }
}

// ── start ───────────────────────────────────────────────────────────────────

/**
 * Start after the app has mounted. `coordinator` is the createFirestoreCacheResetCoordinator()
 * instance main.js already built (one lock, one delete boundary).
 */
export function startBackgroundRefresh({ coordinator } = {}) {
  if (state.started || typeof window === 'undefined') return state
  state.started = true
  state.coordinator = coordinator || null
  state.bootedVersion = getCacheVersion()
  state.knownVersion = state.bootedVersion
  state.lastActivityAt = Date.now()
  state.lastVerdict = null

  for (const eventName of ACTIVITY_EVENTS) {
    window.addEventListener(eventName, noteActivity, { passive: true, capture: true })
  }
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') noteActivity()
    tick()
  })
  window.addEventListener('storage', event => {
    if (event.key === LOCAL_VERSION_KEY) {
      learnVersion(event.newValue)
      tick()
    } else if (event.key === CACHE_RESET_PENDING_KEY) {
      tick()
    }
  })

  try {
    state.unsubscribeVersion = onSnapshot(
      doc(db, 'globalSettings', 'appCacheVersion'),
      snapshot => {
        const data = snapshot.data() || {}
        if (
          learnVersion(data.version, {
            forceReload: data.forceReload === true,
            reason: data.reason,
          })
        )
          tick()
      },
      error => console.warn('[backgroundRefresh] version listener error', error)
    )
  } catch (error) {
    console.warn('[backgroundRefresh] version listener failed to start', error)
  }

  attachDeletionWatcher()

  // Cache a candidate verdict; navigation rechecks conditions before taking over.
  const refreshVerdict = async () => {
    if (!state.started) return
    try {
      state.lastVerdict = await evaluate({ navigating: true })
    } catch {
      state.lastVerdict = null
    }
  }
  state.tickTimer = setInterval(() => {
    tick()
    refreshVerdict()
  }, REFRESH_TICK_MS)
  refreshVerdict()
  log('started', { bootedVersion: state.bootedVersion, mode: currentMode() })
  return state
}

export function backgroundRefreshState() {
  return { ...state, blockedBy: activeRefreshBlockers(), pending: readPendingReset() }
}
